import { useField } from 'formik';
import React__default, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { eventStopPropagation } from '../../lib/event.js';
import { setTestId } from '../../lib/testId.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { Icon } from '../icon/Icon.js';
import { InnerInputLabel, setDisabledDataAttribute } from '../inputs/InputCommon.js';
import { Stack } from '../layout/Stack.js';
import { CSSDefaultInputField } from './common.js';
import { onChangeFieldWrapper, hasError } from './Field.js';
import { setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

const StyledFileInput = styled.input`
  height: 0;
  width: 0;
  overflow: hidden;
`;
const StyledLabel = styled.label`
  ${CSSDefaultInputField}
  cursor: pointer;
`;
const StyledInnerInputLabel = styled(InnerInputLabel)`
  text-align: left;
  top: 1.125rem;
`;
/**
 * File field with Formik Field bindings.
 * Accepts custom + all standard text input props except for `placeholder` - label should be provided instead
 *
 * @example
 * <FileField name="notes" label={'Make a note...'} />
 */ const FileField = ({ fileTypes , label ="Select a file" , name , error , throttleMS , testId ="X" , forwardedRef , children , disabled , reserveMessageSpace =true , onFieldChange , ...props })=>{
    const [{ value  }, meta, { setValue , setTouched  }] = useField(name);
    const set = useCallback(onChangeFieldWrapper(onFieldChange, setValue), [
        onFieldChange,
        setValue
    ]);
    const input = useRef();
    const [fileName, setFileName] = useState("");
    const update = useCallback(()=>{
        if (input?.current?.files) {
            setTouched(true);
            setFileName(input.current.files[0].name);
            set(input.current?.files[0]);
        }
    }, [
        input,
        set,
        setTouched
    ]);
    const remove = useCallback(()=>{
        setTouched(true);
        set(undefined);
        // We have to set the value of the input to blank
        // or the onChange won't trigger if the user
        // selects the same file that was removed.
        if (input?.current) {
            input.current.value = "";
        }
    }, [
        set,
        setTouched
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-text-input": true,
        "data-hpy-web-text-input-has-icon": true,
        ...setDisabledDataAttribute(!!disabled),
        ...setErrorDataAttribute(hasError(meta) || !!error),
        gap: 0.25
    }, React__default.createElement(StyledLabel, null, React__default.createElement(StyledFileInput, {
        ref: input,
        "data-hpy-web-text-input-input": true,
        type: "file",
        accept: fileTypes,
        disabled: disabled,
        onChange: update,
        ...setTestId("FileField-" + testId),
        ...props
    })), React__default.createElement(StyledInnerInputLabel, {
        "data-hpy-web-text-input-input-inner-label": true,
        truncate: true,
        size: "medium"
    }, !!value ? fileName : label), !!value && React__default.createElement(React__default.Fragment, null, React__default.createElement(ButtonIcon, {
        "data-hpy-web-text-input-input-clear-button": true,
        variant: "default",
        testId: testId + "-ClearFileButton",
        icon: "close",
        size: "small",
        onClick: eventStopPropagation(remove)
    })), children, React__default.createElement(Icon, {
        "data-hpy-web-text-input-input-icon": true,
        icon: "attachment"
    }), React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace,
        error: error
    }));
};
FileField.dataId = "data-hpy-web-file-field";
FileField.displayName = "FileField";

export { FileField };
